"use client";

import { UpdateLoginFlowBody } from "@ory/kratos-client";
import Cookies from "js-cookie";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Container, Row } from "react-bootstrap";
import Markdown from "react-markdown";

import { revalidatePathAction } from "@/actions/revalidatePath";
import { LocalizedLink } from "@/components/localized-link";
import { handleFlowError } from "@/components/ory/errors";
import { FlowLogin } from "@/components/ory/flow-login";
import { useFlow } from "@/components/ory/use-flow";
import { StandardPageProps } from "@/components/standard-page";
import { AFTER_AUTH_REDIRECT_COOKIE } from "@/cookies";
import { kratos } from "@/lib/kratos";
import { GetType } from "@/types/types";

export function Login({ content, lang }: StandardPageProps<"auth">) {
  const router = useRouter();

  const onDeleteCookies = async () => {
    const cookieStore = (
      window as Window & {
        cookieStore?: {
          delete: (name: string) => Promise<void>;
          getAll: () => Promise<{ name: string }[]>;
        };
      }
    ).cookieStore;

    if (cookieStore) {
      try {
        const cookies = await cookieStore.getAll();

        for (const cookie of cookies) {
          if (cookie.name) {
            await cookieStore.delete(cookie.name);
          }
        }
      } catch (error) {
        console.error("Failed to delete cookies:", error);
      }
    } else {
      const cookies = document.cookie.split(";");

      for (const cookie of cookies) {
        const eqPos = cookie.indexOf("=");
        const name =
          eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();

        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      }
    }

    router.refresh();
    revalidatePathAction(`/${lang}/account/recovery`)
      .then((_) => router.replace(`/${lang}/account/recovery`))
      .catch((_) => {
        // Do Nothing
      });
  };

  return (
    <Container className="section-container light">
      <Row className="justify-content-center py-4">
        <Col xxl={4} xl={5} lg={6} md={8}>
          <h1 className="text-center mb-5">{content.login_headline}</h1>
          <Suspense fallback={<p className="text-center">{content.loading}</p>}>
            <LoginFlowComponent content={content} lang={lang} />
          </Suspense>
          <p className="mt-4 text-center">
            <LocalizedLink
              href={`#`}
              lang={lang}
              className="user-select-none opacity-50 disabled text-decoration-line-through cursor-disabled"
            >
              {content.registration_link_text}
            </LocalizedLink>{" "}
            |{" "}
            <LocalizedLink href={"/account/verification/"} lang={lang}>
              {content.account_activation_link}
            </LocalizedLink>{" "}
            |{" "}
            <Link onClick={() => void onDeleteCookies()} href={"#"}>
              {content.login_recovery_url_text}
            </Link>
          </p>
        </Col>
      </Row>
    </Container>
  );
}

interface LoginFlowProps {
  content: GetType<"api::auth.auth">;
  lang: string;
}

function LoginFlowComponent({ content, lang }: LoginFlowProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    const urlError = searchParams.get("error");

    if (urlError) {
      sessionStorage.setItem("login_auth_error", urlError);
    }

    const storedError = sessionStorage.getItem("login_auth_error") ?? urlError;

    if (storedError) {
      setAuthError(storedError);

      sessionStorage.removeItem("login_auth_error");
      sessionStorage.removeItem("login_steam_id");

      if (urlError) {
        const url = new URL(window.location.href);
        url.searchParams.delete("error");
        window.history.replaceState({}, "", url.pathname + url.search);
      }
    }
  }, [searchParams]);

  const [flow, setFlow] = useFlow(
    "login",
    (params) => ({
      returnTo: params?.get("return_to") ?? undefined,
      loginChallenge: params?.get("login_challenge") ?? undefined,
      refresh: params?.get("refresh") ? true : undefined,
      aal: params?.get("aal") ?? undefined,
    }),
    (x) => kratos.getLoginFlow(x),
    (x) =>
      kratos.createBrowserLoginFlow({
        ...x,
        returnTo: new URL(
          x.returnTo ??
            Cookies.get(AFTER_AUTH_REDIRECT_COOKIE) ??
            `/${lang}/account/settings`,
          window.location.origin,
        ).href,
      }),
  );

  const csrfToken = useMemo(() => {
    const csrfNode = flow?.ui.nodes.find(
      (node) =>
        "name" in node.attributes && node.attributes.name === "csrf_token",
    );
    return csrfNode && "value" in csrfNode.attributes
      ? String(csrfNode.attributes.value)
      : "";
  }, [flow]);

  const updateFlow = (body: UpdateLoginFlowBody) =>
    kratos
      .updateLoginFlow({
        flow: String(flow?.id),
        updateLoginFlowBody: body,
      })
      .then(() => {
        const path =
          flow?.return_to ??
          Cookies.get(AFTER_AUTH_REDIRECT_COOKIE) ??
          `/${lang}/account/settings`;

        router.refresh();
        Cookies.remove(AFTER_AUTH_REDIRECT_COOKIE);
        revalidatePathAction(new URL(path, window.location.origin).pathname)
          .then(() => {
            window.location.href = path;
          })
          .catch((_) => {
            // Do Nothing
          });
      })
      .catch(handleFlowError(router, "login", setFlow));

  return (
    <>
      {/* ========================================================================= */}
      {/* ERROR ALERTS                                                              */}
      {/* ========================================================================= */}

      {/* INFRASTRUCTURE / BACKEND / KRATOS ADMIN OUTAGE */}
      {(authError === "service_unavailable" ||
        authError === "server_error") && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setAuthError(null)}
          className="text-start mb-4 shadow-sm bg-dark border-0"
        >
          <Alert.Heading as="h5" className="fw-bold mb-2 text-danger">
            {content.error_login_currently_not_possible}
          </Alert.Heading>
          <p className="mb-2 small text-light">
            {content.connection_to_auth_service_failed}
          </p>
          <p className="mb-0 small text-muted">
            {content.connection_error_retry_info}
          </p>
        </Alert>
      )}

      {/* STEAM OPENID SIGNATURE OR HANDSHAKE FAILED */}
      {authError === "steam_auth_failed" && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setAuthError(null)}
          className="text-start mb-4 shadow-sm bg-dark border-0"
        >
          <Alert.Heading as="h5" className="fw-bold mb-2 text-danger">
            {content.error_steam_verification_failed}
          </Alert.Heading>
          <p className="mb-0 small text-light">
            {content.steam_verification_error_info}
          </p>
        </Alert>
      )}

      {/* BUSINESS GATEKEEPER: STEAM IN-GAME ACCOUNT REQUIRED */}
      {authError === "steam_game_account_required" && (
        <Alert
          variant="warning"
          dismissible
          onClose={() => setAuthError(null)}
          className="text-start mb-4 shadow-sm bg-dark border-0"
        >
          <Alert.Heading as="h5" className="fw-bold mb-2 text-light">
            {content.error_no_game_account_found}
          </Alert.Heading>
          <p className="mb-2 small text-light">
            {content.no_game_account_found_error_info}
          </p>
          <p className="mb-0 small text-muted">
            {content.no_game_account_steam_registration_info}
          </p>
        </Alert>
      )}

      {/* UNKNOWN / GENERIC FALLBACK (IF AN UNRECOGNIZED ERROR REACHES FRONTEND) */}
      {authError &&
        ![
          "service_unavailable",
          "server_error",
          "steam_auth_failed",
          "steam_game_account_required",
        ].includes(authError) && (
          <Alert
            variant="secondary"
            dismissible
            onClose={() => setAuthError(null)}
            className="text-start mb-4 shadow-sm bg-dark border-0"
          >
            <Alert.Heading as="h5" className="fw-bold mb-2 text-light">
              {content.generic_login_error}
            </Alert.Heading>
            <p className="mb-0 small text-muted">
              {content.generic_login_error_info}
            </p>
          </Alert>
        )}

      {/* ========================================================================= */}
      {/* STEAM LOGIN BUTTON & INFO                                                 */}
      {/* ========================================================================= */}
      {flow?.ui.action && (
        <div className="mb-4">
          <form action={flow.ui.action} method="POST">
            <input type="hidden" name="csrf_token" value={csrfToken} />
            <input type="hidden" name="method" value="oidc" />
            <Button
              type="submit"
              name="provider"
              value="steam-native"
              className="w-100 py-2 d-flex align-items-center justify-content-center gap-2 border-1 border-light shadow-sm"
              style={{
                backgroundColor: "#171a21",
                color: "#ffffff",
                fontWeight: 600,
                fontSize: "0.95rem",
                borderRadius: "6px",
                transition: "background-color 0.2s ease-in-out",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#2a475e")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#171a21")
              }
            >
              {/* Steam SVG Icon */}
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.005.105.005.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.723L.436 14.717C1.698 19.997 6.402 24 11.979 24c6.627 0 12-5.373 12-12s-5.373-12-12-12zm-3.61 17.58c-1.031 0-1.87-.838-1.87-1.872 0-.324.084-.628.23-.895l1.974.816a1.306 1.306 0 0 0 1.267.078 1.31 1.31 0 0 0 .752-1.168c0-.03-.005-.058-.007-.087l1.986.821c-.042.179-.065.365-.065.557 0 1.034-.84 1.87-1.87 1.87zm7.625-6.848c-1.396 0-2.528-1.134-2.528-2.529 0-1.396 1.132-2.531 2.528-2.531 1.397 0 2.532 1.135 2.532 2.531 0 1.395-1.135 2.529-2.532 2.529z" />
              </svg>
              {content.label_login_with_steam}
            </Button>
          </form>

          {/* Steam Info Notice */}
          <div
            className="mt-2 text-start p-2 rounded bg-light border border-secondary"
            style={{ fontSize: "0.78rem", lineHeight: "1.4" }}
          >
            <Markdown>{content.steam_info_login_text}</Markdown>
          </div>

          {/* Divider */}
          <div className="d-flex align-items-center my-4">
            <hr className="flex-grow-1 my-0 opacity-25" />
            <span className="px-3 text-muted small text-uppercase fw-semibold">
              {content.or}
            </span>
            <hr className="flex-grow-1 my-0 opacity-25" />
          </div>
        </div>
      )}

      {/* Standard Email / Password Form */}
      <FlowLogin
        flow={flow}
        onSubmit={updateFlow}
        content={content}
        lang={lang}
      />
    </>
  );
}
